import { css } from '@emotion/react';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import React, { CSSProperties } from 'react';
import { kpiRequestStatusToColor } from '../../../data-models/kpi-requests.data-model';
import {
  CompanyPeriodRequestStatus,
  DataPointPeriodicity,
} from '../../../schemas/CompanyPeriodRequest.schema';
import { colors } from '../../../theme/colors';
import { FMT } from '../../../util/formatter-service';
import { KpiRequestChart } from '../components/KPIRequests/Manage/KpiManageChart';
import { FrequencyPeriodFilter } from './FrequencyPeriodFilter';
import { AvailablePeriod } from './manage-request-utils';

const wrapperCss = css`
  align-items: center;
  column-gap: 1rem;
  display: grid;
  width: 100%;
  min-width: 0;
`;

const toggleButtonCss = css`
  &:not(:disabled) {
    overflow: hidden;
    position: relative;
    transition: color 0.25s ease;

    /* Status-coloured strip along the bottom (matches the chart bar)… */
    &::before {
      background-color: var(--btn-color);
      bottom: 0;
      content: '';
      height: 3px;
      left: 0;
      position: absolute;
      right: 0;
      transition: height 0.25s ease;
    }

    /* Keep the label above the fill. */
    .toggle-label {
      position: relative;
      z-index: 1;
    }

    &:hover:not(.Mui-selected)::before {
      height: 5px;
    }

    /* …that fills the whole button when selected, text transitioning to white. */
    &.Mui-selected {
      background-color: transparent;
      color: ${colors.primary[0]};

      &::before {
        height: 100%;
      }
    }
  }
`;

interface KpiManageMenuBarV2Props {
  allStatuses: CompanyPeriodRequestStatus[];
  requestStats: Map<CompanyPeriodRequestStatus | string, number>;
  selectedStatus: string;
  onSelectedStatusChange: (status: string) => void;
  selectedFrequency: DataPointPeriodicity;
  onFrequencyChange: (frequency: DataPointPeriodicity) => void;
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  availablePeriods: AvailablePeriod[];
  rightAction?: React.ReactNode;
}

export function KpiManageMenuBarV2({
  allStatuses,
  requestStats,
  selectedStatus,
  onSelectedStatusChange,
  selectedFrequency,
  onFrequencyChange,
  selectedPeriod,
  onPeriodChange,
  availablePeriods,
  rightAction,
}: KpiManageMenuBarV2Props) {
  const onChangeSelectedStatus = (_: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
    // Exclusive group — always keep one item selected.
    if (newStatus !== null) {
      onSelectedStatusChange(newStatus);
    }
  };

  const statusToggles = allStatuses.map((status) => (
    <ToggleButton
      key={status}
      value={status}
      disabled={!requestStats.has(status)}
      css={toggleButtonCss}
      style={{ '--btn-color': kpiRequestStatusToColor(status) } as CSSProperties}
    >
      <span className='toggle-label'>{FMT.format('kpiStatus', status)}</span>
    </ToggleButton>
  ));
  statusToggles.unshift(
    <ToggleButton
      key='all'
      value='All'
      css={toggleButtonCss}
      style={{ '--btn-color': colors.primary[60] } as CSSProperties}
    >
      <span className='toggle-label'>All</span>
    </ToggleButton>
  );

  return (
    <div css={wrapperCss}>
      <ToggleButtonGroup value={selectedStatus} onChange={onChangeSelectedStatus} exclusive fullWidth>
        {statusToggles}
      </ToggleButtonGroup>
      <div style={{ width: '100%', height: '30px', minWidth: 0 }}>
        <KpiRequestChart data={requestStats} allStatuses={allStatuses} selectedStatus={selectedStatus} />
      </div>
      <Stack
        direction='row'
        alignItems='center'
        gap='1rem'
        width='100%'
        // Breathing room so the filters aren't squashed between the status bar and the grid.
        sx={{ py: 1.5 }}
      >
        <div style={{ whiteSpace: 'nowrap' }}>
          {`Showing ${requestStats.get(selectedStatus) ?? 0} requests for:`}
        </div>
        <FrequencyPeriodFilter
          selectedFrequency={selectedFrequency}
          onFrequencyChange={onFrequencyChange}
          selectedPeriod={selectedPeriod}
          onPeriodChange={onPeriodChange}
          availablePeriods={availablePeriods}
        />
        {rightAction && <div style={{ marginLeft: 'auto' }}>{rightAction}</div>}
      </Stack>
    </div>
  );
}
